// Angular
import {
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// RxJS
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';

// Component Library
import { FsxOption, FsxSelectComponent } from 'fsx-component-library';

// Data Contracts
import { ClerkReviewCourt } from '@data-contracts';

// Local
import { ClerkReviewFilterFormService } from '@feature/crl/clerk-review-filter-form.service';
import { ClerkReviewFilterPayloadService } from '@feature/crl/clerk-review-filter-payload.service';
import { ClerkReviewFilterStoreValuesService } from '@feature/crl/clerk-review-filter-store-values.service';

/**
 * A constant to use for the default "All Courts" option..
 */
export const ALL_COURTS_VALUE = 0;

@Component({
  selector: 'app-court-filter-control',
  standalone: true,
  imports: [ReactiveFormsModule, FsxSelectComponent, FormsModule],
  templateUrl: './court-filter-control.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./court-filter-control.component.scss'],
})
export class CourtFilterControlComponent implements OnInit, OnDestroy {
  /*********************************************************************************************************************
   * Service Injections
   ********************************************************************************************************************/
  private readonly formService = inject(ClerkReviewFilterFormService);
  private readonly payloadService = inject(ClerkReviewFilterPayloadService);
  private readonly storeValuesService = inject(
    ClerkReviewFilterStoreValuesService,
  );

  /**
   * The collection of objects from which we can derive a collection
   * of dropdown Option objects.
   */
  @Input() courts: ClerkReviewCourt[] = [];

  /**
   * The value of the Option that should be selected by default.
   * - Set to the "All Courts" option value when multiple court options are available.
   * - Set to single option value when only one court option is available.
   */
  defaultValue = ALL_COURTS_VALUE;

  /**
   * The FormControl for the "Court" dropdown.
   */
  formControl = this.formService.courtFormControl;

  /**
   * The collection of dropdown Option objects to pass to the FsxSelectComponent.
   */
  courtOptions: FsxOption<number>[] = [];

  /**
   * The Subject used to teardown the filter control listener subscriptions.
   */
  private destroy$$ = new Subject<void>();

  /**
   * The Angular lifecycle hook where we can safely run any code dependent on @Input() bindings.
   */
  ngOnInit(): void {
    const storedValue = this.storeValuesService.getStoredCourtValue();
    this.deriveDropdownOptions();
    this.setDefaultValue();
    this.setValue(storedValue);
    this.startListeningForChanges();
  }

  /**
   * The Angular lifecycle hook for tearing down the subscriptions with the component.
   */
  ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  /**
   * A private method to set up the FormControl valueChanges() listener.
   */
  private startListeningForChanges(): void {
    this.formControl.valueChanges
      .pipe(takeUntil(this.destroy$$), distinctUntilChanged())
      .subscribe((value: number) => {
        // Use the emitted value to set the Court filter payload.
        this.payloadService.setCourtFilter(value);
      });
  }

  /**
   * A private method to derive the dropdown Options from the passed in courts collection.
   */
  private deriveDropdownOptions(): void {
    // Derive the Court Options from the clerkReviewFilterSettings.courts collection.
    this.courtOptions = this.courts.map((court: ClerkReviewCourt) => {
      const option: FsxOption<number> = {
        value: court.courtId,
        label: court.courtName,
      };
      return option;
    });

    // Conditionally Prepend the "All Courts" option.
    if (this.courtOptions.length > 1) {
      // Initialize the "All Courts" Option.
      const allCourtsOption: FsxOption<number> = {
        value: ALL_COURTS_VALUE,
        label: 'All Courts',
      };

      // Add the "All Courts" Option to the beginning of the courtOptions collection.
      this.courtOptions.unshift(allCourtsOption);
    }
  }

  /**
   * A private method to determine which option value should be used as the default value.
   */
  private setDefaultValue(): void {
    // Guard clause to prevent trying to set a value when no dropdown options were derived.
    if (this.courtOptions.length === 0) {
      console.warn('There must be at least 1 court option to set a default.');
      return;
    }

    // Set the default value here. Always the first item in the list.
    this.defaultValue = this.courtOptions[0].value;
  }

  /**
   * A public method to allow a new FormControl value to be set.
   * - If no value is provided or if the value is falsy then it falls back to the default value.
   *
   * @param {number | null} newValue - The new value to set.
   */
  setValue(newValue: number | null = null): void {
    const selectedValue = newValue || this.defaultValue; // Use provided value or fallback to default
    this.formControl.setValue(selectedValue); // Update FormControl with the selected value
  }
}
